'use client';

import { useTranslations } from 'next-intl';
import { useState, useEffect } from 'react';

import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { UiKitPanelBase } from '@/fo/ui/kit/panel/base';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';

import { styles } from './UiAuthModalBase.styles';

export interface UiAuthModalBaseProps {
  loginForm: React.ReactNode;
  registrationForm: React.ReactNode;
  forgotPasswordForm: React.ReactNode;
  onTabChange?: (name: 'login' | 'registration') => void;
}

export function UiAuthModalBase({
  loginForm,
  registrationForm,
  forgotPasswordForm,
  onTabChange
}: UiAuthModalBaseProps) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'tabs' | 'forgotPassword'>('tabs');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showInformer, setShowInformer] = useState(false);

  const slots = styles({
    informerState: showInformer && forgotPasswordEmail ? 'visible' : 'hidden'
  });

  useEffect(() => {
    const unsubscribeOnOpen = utilEventAuthModal.onOpen(() => {
      setOpen(true);
      setView('tabs');
    });
    const unsubscribeOnClose = utilEventAuthModal.onClose(() => {
      setOpen(false);
    });
    const unsubscribeOnShowForgotPassword =
      utilEventAuthModal.onShowForgotPassword(() => setView('forgotPassword'));
    const unsubscribeOnShowLogin = utilEventAuthModal.onShowLogin((email) => {
      setForgotPasswordEmail(email || '');
      setView('tabs');

      if (email) {
        setShowInformer(false);
        const timer = setTimeout(() => {
          setShowInformer(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    });

    return () => {
      unsubscribeOnOpen();
      unsubscribeOnClose();
      unsubscribeOnShowForgotPassword();
      unsubscribeOnShowLogin();
    };
  }, []);

  useEffect(() => {
    if (showInformer && forgotPasswordEmail) {
      const timer = setTimeout(() => {
        setShowInformer(false);
      }, 15000);

      return () => clearTimeout(timer);
    }
  }, [showInformer, forgotPasswordEmail]);

  return (
    <UiKitDrawerBase
      title={t('Personal account')}
      open={open}
      direction="right"
      onOpenChange={setOpen}
      withoutMobile
    >
      {view === 'tabs' && (
        <>
          <div className={slots.title()}>{t('Personal account')}</div>
          <UiKitPanelBase
            view="tab"
            tabClassName={slots.tab()}
            items={[
              {
                content: loginForm,
                header: t('login')
              },
              {
                content: registrationForm,
                header: t('registration')
              }
            ]}
            onTabChange={(index) => {
              onTabChange?.(index === 0 ? 'login' : 'registration');
            }}
          />
          <p className={slots.informer()}>
            {t('If we have an account registered to an email')}{' '}
            {forgotPasswordEmail},{' '}
            {t('you will receive an email with a link to reset your password')}
          </p>
        </>
      )}
      {view === 'forgotPassword' && forgotPasswordForm}
    </UiKitDrawerBase>
  );
}
